// @flow

import ko from 'rgs-knockout';
import page from 'page-common';
import type { Observable, ObservableArray } from 'rgs-knockout/types';
import { ObservableBusy } from 'observable-busy';
import { observableISODate } from 'observable-iso-date';
import { AdvancedDialog } from 'app/v2/common/features/dialog/implementations/advance-dialog';

import type { FetchContractsParams } from '../../services/contracts';
import { AccidentContractService } from '../../services/contracts';
import { AccidentContract } from '../../services/contracts/accident-contract-model';
import { AccidentMailingService } from '../../services/mailing';
import {
	certificateMailingActionTypes,
	certificateMailingActionTypesText,
} from '../../constants/certificate-mailing-action-types';
import { ContractSelect } from '../../pages/admin/ko-components/contract-select';

import { CertificateMailingDialogViewModel } from './certificate-mailing-dialog-view-model';
import CertificateMailingTemplate from './certificate-mailing-dialog.html';

export type CertificateMailingDialogOptions = {
	contractsService: AccidentContractService;
	mailingService: AccidentMailingService;
	accidentContractId: number;
	onCreateMailing?: () => void;
}

export class CertificateMailingDialog extends AdvancedDialog {

	_options: CertificateMailingDialogOptions;
	_contractsService: AccidentContractService;
	_mailingService: AccidentMailingService;
	_viewModel: CertificateMailingDialogViewModel;
	_busy: ObservableBusy;

	props: CertificateMailingDialogViewModel & {
		isDrafted: Observable<boolean>;
		contractSelect: ContractSelect;
		contractNumber: Observable<string>;
		actionTypeOptions: ObservableArray<{
			value: string;
			text: string;
		}>;
		actionType: Observable<string>;
		selectedActionType: Observable<string>;
		insurer: Observable<string>;
		contractStartDate: observableISODate;
		isNoCertificateForContract: Observable<boolean>;
		busy: ObservableBusy;
	};

	constructor (options: CertificateMailingDialogOptions) {
		super();

		this._options = options;

		this._contractsService = this._options.contractsService;
		this._mailingService = this._options.mailingService;

		this._busy = new ObservableBusy();
		this.contractStartDate = observableISODate();
		this._viewModel = new CertificateMailingDialogViewModel({
			contractStartDate: this.contractStartDate,
		});

		this.props = {
			...this._viewModel,
			isDrafted: ko.observable(false),
			contractSelect: this._createContractSelect(),
			contractNumber: ko.observable(''),
			actionTypeOptions: ko.observableArray(
				certificateMailingActionTypes.map(type => ({
					value: type,
					text: certificateMailingActionTypesText[type],
				}))
			),
			selectedActionType: ko.pureComputed(
				() => certificateMailingActionTypesText[this.props.ActionType()]
			),
			insurer: ko.observable(''),
			contractStartDate: this.contractStartDate,
			isNoCertificateForContract: ko.pureComputed(() =>
				this.props.TotalCertificatesCount() === 0),
			busy: this._busy,
			onSubmit: this._submitHandler,
			sendDraftMailing: this._sendDraftMailing,
			cancelDraftMailing: this._cancelDraftMailing,
		};
		this._setSelectedContract();
	}

	_createContractSelect (): ContractSelect {
		return new ContractSelect({
			fetchSearch: this._fetchContractsBySearchWord,
			onItemClick: this._onSelectContract,
			onCancel: this._resetContractData.bind(),
		});
	}

	_fetchContractsBySearchWord = (params: FetchContractsParams) => {
		return this._contractsService.fetchContracts(params)
			.then(({ Data }) => Data)
			.catch(e => {
				throw e;
			});
	};

	_onSelectContract = (contract: AccidentContract) => {
		this.contractStartDate(contract.StartDate);
		this.props.contractNumber(contract.ContractNumber);
		this.props.AccidentContractID(contract.AccidentContractID);
		this.props.InsurancePeriodEnd(contract.EndDate);
		this.props.insurer(contract.ClientName);
	};

	_resetContractData = () => {
		this.contractStartDate('');
		this.props.contractNumber('');
		this.props.AccidentContractID('');
		this.props.InsurancePeriodEnd('');
		this.props.insurer('');
	};

	_setSelectedContract = () => {
		if (this._options.accidentContractId) {
			this._contractsService.fetchContract(this._options.accidentContractId)
				.then(contract => {
					this.props.contractSelect.setCurrentValue(contract);
					this._onSelectContract(contract);
				});
		}
	};

	_sendDraftMailing = () => {
		if (!this._viewModel.validationGroup().guard()) {
			if (!this._viewModel.InsurancePeriodStart()) {
				this._viewModel.InsurancePeriodStart(this.contractStartDate());
			}
			const params = this._viewModel.toJS();

			return this._busy
				.add(this._mailingService.createMailingDraft(params)
					.then(({ TotalCertificatesCount }) => {
						this.props.TotalCertificatesCount(TotalCertificatesCount);
						this.props.isDrafted(TotalCertificatesCount);
					}))
				.done();
		}
	};

	_cancelDraftMailing = () => {
		this.props.isDrafted(false);
	};

	_submitHandler = () => {
		if (!this._viewModel.validationGroup().guard()) {
			const params = this._viewModel.toJS();
			this.hideDialog();

			return this._busy
				.add(this._mailingService.createMailing(params)
					.then((data) => {
						const newMailingListID = data.MailingListID;
						if (this._options.onCreateMailing) {
							this._options.onCreateMailing();
						}
						// eslint-disable-next-line max-len
						const successMessage = `Рассылка сертификатов №${newMailingListID} успешно создана. Отчёт вы можете найти во вкладке "Рассылки сертификатов"`;
						page.notifications.push({
							css: { 'notification-success': true },
							message: successMessage,
						});
					})
					.catch(err => {
						this._handleError(
							err,
							// eslint-disable-next-line max-len
							'Возникла ошибка при создании рассылки. Пожалуйста, попробуйте ещё раз позднее или обратитесь в службу технической поддержки сайта.'
						);
					}))
				.done();
		}
	};

	_handleError = (err, message) => {
		const {status, statusText} = err.response;

		if (status && statusText) {
			if (statusText) {
				err.handled = true;
				page.notifications.push({
					css: { 'notification-danger': true },
					message: statusText,
				});
			}
		}
		if (message) {
			err.userFriendlyMessages = [message];

			// IE doesn't handle notification on throw err
			page.notifications.push({
				css: { 'notification-danger': true },
				message: message,
			});
		}

		throw err;
	};

	getDialogTemplate () {
		return CertificateMailingTemplate();
	}
}

